import { useId } from 'react'
import PropTypes from 'prop-types'

const stars = [
  [4, 4, true, true],
  [4, 44, true],
  [36, 22],
  [50, 146, true, true],
  [64, 43, true, true],
  [76, 30, true],
  [101, 116],
  [140, 36, true],
  [149, 134],
  [162, 74, true],
  [171, 96, true, true],
  [210, 56, true, true],
  [235, 90],
  [275, 82, true, true],
  [306, 6],
  [307, 64, true, true],
  [380, 68, true],
  [380, 108, true, true],
  [391, 148, true, true],
  [405, 18, true],
  [412, 86, true, true],
  [426, 210, true, true],
  [427, 56, true, true],
  [538, 138],
  [563, 88, true, true],
  [611, 154, true, true],
  [637, 150],
  [651, 146, true],
  [682, 70, true, true],
  [683, 128],
  [781, 82, true, true],
  [785, 158, true],
  [832, 146, true, true],
  [852, 89],
]

const constellations = [
  [
    [247, 103],
    [261, 86],
    [307, 104],
    [357, 36],
  ],
  [
    [586, 120],
    [516, 100],
    [491, 62],
    [440, 107],
    [477, 180],
    [516, 100],
  ],
  [
    [733, 100],
    [803, 120],
    [879, 113],
    [823, 164],
    [803, 120],
  ],
]

function Star({ blurId, point: [cx, cy, dim, blur] }) {
  const delay = Math.random() * 2
  const duration = Math.random() * 2 + 2

  return (
    <circle
      cx={cx}
      cy={cy}
      r={1}
      style={{
        transformOrigin: `${cx / 16}rem ${cy / 16}rem`,
        animation: `star-fade-in 4s ${delay}s forwards, ${dim ? 'star-twinkle-dim' : 'star-twinkle'} ${duration}s ${delay + 4}s infinite alternate`,
      }}
      filter={blur ? `url(#${blurId})` : undefined}
    />
  )
}

Star.propTypes = {
  blurId: PropTypes.string.isRequired,
  point: PropTypes.array.isRequired,
}

function Constellation({ points, blurId }) {
  const uniquePoints = points.filter(
    (point, pointIndex) =>
      points.findIndex((p) => String(p) === String(point)) === pointIndex,
  )
  const isFilled = uniquePoints.length !== points.length
  const drawDelay = Math.random() * 3 + 2
  const drawDuration = 5

  return (
    <>
      <path
        stroke="white"
        strokeOpacity="0.2"
        strokeDasharray={1}
        strokeDashoffset={1}
        pathLength={1}
        fill={isFilled ? 'rgb(255 255 255 / 0.02)' : 'transparent'}
        d={`M ${points.join('L')}`}
        style={{
          animation: `constellation-draw ${drawDuration}s ${drawDelay}s forwards${isFilled ? `, constellation-fill 1s ${drawDelay + drawDuration}s forwards` : ''}`,
          visibility: 'hidden',
        }}
      />
      {uniquePoints.map((point, pointIndex) => (
        <Star key={pointIndex} point={point} blurId={blurId} />
      ))}
    </>
  )
}

Constellation.propTypes = {
  points: PropTypes.array.isRequired,
  blurId: PropTypes.string.isRequired,
}

function HorizontalStarField({ className }) {
  let blurId = useId()

  return (
    <svg
      viewBox="0 0 881 211"
      fill="white"
      aria-hidden="true"
      className={`pointer-events-none absolute w-220.25 origin-top-left rotate-0 overflow-visible opacity-70 ${className}`}
    >
      <defs>
        <filter id={blurId}>
          <feGaussianBlur in="SourceGraphic" stdDeviation=".5" />
        </filter>
      </defs>
      {constellations.map((points, constellationIndex) => (
        <Constellation
          key={constellationIndex}
          points={points}
          blurId={blurId}
        />
      ))}
      {stars.map((point, pointIndex) => (
        <Star key={pointIndex} point={point} blurId={blurId} />
      ))}
    </svg>
  )
}

HorizontalStarField.propTypes = {
  className: PropTypes.string,
}

function Glow() {
  let id = useId()

  return (
    <div className="absolute inset-0 -z-10 overflow-hidden bg-gray-950">
      <svg
        className="absolute -top-[20%] left-0 h-[250%] w-full"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id={`${id}-mobile`} cy="0%">
            <stop offset="0%" stopColor="rgba(56, 189, 248, 0.5)" />
            <stop offset="40%" stopColor="rgba(0, 71, 255, 0.15)" />
            <stop offset="100%" stopColor="rgba(10, 14, 23, 0)" />
          </radialGradient>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill={`url(#${id}-mobile)`}
        />
      </svg>
    </div>
  )
}

function PageHeader({ title, subtitle, stats, onCreateIdea, onLogout, onHelp, onFeedback, navItems, transparentStats, compact }) {
  return (
    <div className={`relative overflow-hidden ${compact ? 'pb-0' : 'pb-16 md:pb-32'}`}>
      <Glow />
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full">
          <HorizontalStarField className="top-14" />
        </div>
      </div>
      
      <nav className="bg-gray-800 bg-transparent relative z-10">
        <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="border-b border-white/10">
            <div className="flex h-16 items-center justify-between px-4 sm:px-0">
              <div className="flex items-center flex-1 justify-between md:justify-start">
                <div className="shrink-0 flex items-center">
                  <img src={`${import.meta.env.BASE_URL}images/logo.png`} alt="Opptora Logo" className="size-8" />
                  <span className="text-white font-semibold text-2xl ml-2">Opptora</span>
                </div>
                {navItems && (
                  <div className="md:ml-10 flex items-baseline space-x-4">
                    {navItems}
                  </div>
                )}
              </div>
              <div className="hidden md:block">
                <div className="ml-4 flex items-center md:ml-6 gap-3">
                  {onFeedback && (
                    <button
                      onClick={onFeedback}
                      className="rounded-md bg-gradient-to-br from-purple-500/60 to-purple-600/40 px-4 py-2 text-sm font-display font-light tracking-tight text-white hover:from-purple-500/50 hover:to-purple-600/30 flex items-center gap-1.5"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
                      </svg>
                      Feedback
                    </button>
                  )}
                  {onCreateIdea && (
                    <button
                      onClick={onCreateIdea}
                      className="rounded-md bg-gradient-to-br from-blue-500/60 to-blue-600/40 px-4 py-2 text-sm font-display font-light tracking-tight text-white hover:from-blue-500/50 hover:to-blue-600/30"
                    >
                      + New Idea
                    </button>
                  )}
                  {onHelp && (
                    <button 
                      onClick={onHelp}
                      className="rounded-md px-3 py-2 text-sm font-display font-light tracking-tight text-gray-300 hover:bg-white/5 hover:text-white flex items-center gap-1.5"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-5">
                        <path d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Help
                    </button>
                  )}
                  {onLogout && (
                    <button 
                      onClick={onLogout}
                      className="relative flex items-center gap-2 rounded-md px-3 py-2 text-sm font-display font-light tracking-tight text-gray-300 hover:bg-white/5 hover:text-white"
                    >
                      <span>Logout</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-5">
                        <path d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <header className="py-6 md:py-10 relative z-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-3 md:mt-8 w-full">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight text-white">{title}</h1>
              {subtitle && (
                <p className="text-base text-gray-300 mt-1">{subtitle}</p>
              )}
            </div>
            {stats && (
              <div className={`flex items-center gap-6 text-base text-white/80 rounded-2xl px-4 py-2 min-h-[40px] ${transparentStats ? 'bg-transparent shadow-none' : 'bg-gradient-to-br from-gray-100/10 to-gray-200/10 shadow-xl dark:bg-gradient-to-br dark:from-gray-700/25 dark:to-gray-700/15 dark:shadow-none'}`}>
                {stats}
              </div>
            )}
          </div>
        </div>
      </header>
    </div>
  )
}

PageHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  stats: PropTypes.node,
  onCreateIdea: PropTypes.func,
  onLogout: PropTypes.func,
  onHelp: PropTypes.func,
  onFeedback: PropTypes.func,
  navItems: PropTypes.node,
  transparentStats: PropTypes.bool,
  compact: PropTypes.bool,
}

export default PageHeader
